import React, { createContext, useState, useContext, useCallback } from 'react';
import { supabase } from '../data/supabaseClient';

const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [lastConversationId, setLastConversationId] = useState(null);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [unreadConversations, setUnreadConversations] = useState(new Set());
  const [showConversationList, setShowConversationList] = useState(true);
  const [otherUnreadMessages, setOtherUnreadMessages] = useState(new Set());
  const [messageSubscriptions, setMessageSubscriptions] = useState(new Map());
  // Store occurrence context for recurring posts
  const [occurrenceContext, setOccurrenceContext] = useState(null); // { occurrenceId, occurrenceDate }

  const addNotification = useCallback((notification) => {
    const { conversationId, sender, timestamp } = notification;
  
    if (isChatOpen && activeConversationId === conversationId) {
      return;
    }
  
    setUnreadConversations((prev) => new Set([...prev, conversationId]));
  
    if (!isChatOpen) {
      setNotificationQueue((prev) => {
        const newQueue = [...prev, { 
          id: Date.now(), 
          conversationId, 
          sender, 
          timestamp 
        }];
        return newQueue.slice(-3);
      });
  
      setTimeout(() => {
        setNotificationQueue((prev) => 
          prev.filter((n) => n.id !== notification.id)
        );
      }, 3000);
    }
  }, [isChatOpen, activeConversationId]);

  const addOtherUnreadMessage = useCallback((conversationId) => {
    console.log('Adding unread message for conversation:', conversationId, 'Current active:', activeConversationId);
    
    if (conversationId !== activeConversationId) {
      setOtherUnreadMessages(prev => {
        const newSet = new Set(prev);
        newSet.add(conversationId);
        console.log('Updated unread messages:', Array.from(newSet));
        return newSet;
      });
    }
  }, [activeConversationId]);

  const clearOtherUnreadMessage = useCallback((conversationId) => {
    console.log('Clearing unread message for conversation:', conversationId);
    setOtherUnreadMessages(prev => {
      const newSet = new Set(prev);
      newSet.delete(conversationId);
      return newSet;
    });
  }, []);
  

  const markConversationAsRead = useCallback((conversationId) => {
    setUnreadConversations((prev) => {
      const updated = new Set(prev);
      updated.delete(conversationId);
      return updated;
    });
  }, []);

  // Function to check if user can start a new conversation based on their limits
  const checkCanStartChat = useCallback(async () => {
    try {
      // First check if we have a valid user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user');
        return { canChat: false, limitData: null, error: 'Not authenticated' };
      }
      
      // Use the get_user_chat_usage function directly
      const { data, error } = await supabase.rpc('get_user_chat_usage', {
        p_user_id: user.id
      });
        
      if (error) {
        console.error('Error checking chat limits:', error);
        return { canChat: true, limitData: null, error };
      }
      
      // If no data returned, default to allowing chats
      if (!data) {
        return { 
          canChat: true, 
          limitData: {
            plan_type: 'free',
            remaining: 5,
            max_allowed: 5,
            has_remaining: true,
            monthly_count: 0,
            last_reset: new Date().toISOString()
          },
          error: null
        };
      }
      
      const limitData = {
        plan_type: data.plan_type || 'free',
        remaining: data.remaining_chats || 0,
        max_allowed: data.monthly_limit || 5,
        has_remaining: (data.remaining_chats > 0) || (data.monthly_limit >= 9999),
        monthly_count: data.monthly_count || 0,
        last_reset: data.last_reset || new Date().toISOString()
      };
      
      return { 
        canChat: limitData.has_remaining, 
        limitData,
        error: null
      };
    } catch (error) {
      console.error('Error checking chat limits:', error);
      return { canChat: true, limitData: null, error };
    }
  }, []);
  
  const openChatWithConversation = useCallback((conversationId, occurrenceData = null) => {
    setIsChatOpen(true);
    setActiveConversationId(conversationId);
    setShowConversationList(false); // This will make it go directly to chat

    // Store occurrence context if provided (for recurring posts)
    if (occurrenceData) {
      setOccurrenceContext(occurrenceData);
    } else {
      setOccurrenceContext(null);
    }

    if (conversationId) {
      markConversationAsRead(conversationId);
    }
  }, [markConversationAsRead]);

  // Function to open chat with a specific user (for notifications)
  const openChat = useCallback(async (conversationId, otherUserId) => {
    console.log('Opening chat with conversation:', conversationId, 'user:', otherUserId);
    
    if (conversationId) {
      // If we have a conversation ID, open it directly
      openChatWithConversation(conversationId);
    } else if (otherUserId) {
      // If we only have a user ID, find or create a conversation
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          console.error('No authenticated user');
          return;
        }

        // First, try to find an existing conversation between these two users
        const { data: existingConversations, error: searchError } = await supabase
          .from('conversation_participants')
          .select(`
            conversation_id,
            conversations!inner(
              id,
              participant_ids
            )
          `)
          .eq('user_id', user.id)
          .eq('conversations.participant_ids', otherUserId);

        if (searchError) {
          console.error('Error searching for existing conversation:', searchError);
        }

        if (existingConversations && existingConversations.length > 0) {
          // Found existing conversation, open it
          const conversationId = existingConversations[0].conversation_id;
          console.log('Found existing conversation:', conversationId);
          openChatWithConversation(conversationId);
        } else {
          // No existing conversation, we'll need to create one
          // For now, just open the chat and let the user start a new conversation
          console.log('No existing conversation found, opening chat to create new one');
          setIsChatOpen(true);
          setShowConversationList(true);
        }
      } catch (error) {
        console.error('Error opening chat:', error);
        // Fallback: just open the chat interface
        setIsChatOpen(true);
        setShowConversationList(true);
      }
    } else {
      // No conversation ID or user ID, just open the chat interface
      setIsChatOpen(true);
      setShowConversationList(true);
    }
  }, [openChatWithConversation]);

  const showConversations = useCallback(() => {
    setShowConversationList(true);
    setActiveConversationId(null);
  }, []);

  const closeChat = useCallback(() => {
    setIsChatOpen(false);
    setActiveConversationId(null);
    setShowConversationList(true); // Reset to show list when reopening
  }, []);

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev);
  }, []);
  

  return (
    <ChatContext.Provider
      value={{
        isChatOpen,
        activeConversationId,
        showConversationList,
        notificationQueue,
        unreadConversations,
        openChatWithConversation,
        openChat, // Add the new openChat function
        closeChat,
        toggleChat,
        addNotification,
        markConversationAsRead,
        showConversations,
        otherUnreadMessages,
        addOtherUnreadMessage,
        clearOtherUnreadMessage,
        messageSubscriptions,
        setMessageSubscriptions,
        checkCanStartChat, // Add the new function to the context
        occurrenceContext, // Expose occurrence context for recurring posts
        setOccurrenceContext, // Allow clearing/setting occurrence context
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};